import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { TFunction } from 'next-i18next';
import { useTranslation } from 'next-i18next';
import memoize from 'memoize-one';
import Head from 'next/head';
import { sprintf } from 'sprintf-js';

import { openNewRequestModal } from 'modules/requests/service';
import Layout from 'modules/layout';
import LandingPageDefault from 'modules/marketing-pages/components/LandingPage/LandingPageDefault';
import type { LandingPageDefaultProps } from 'modules/marketing-pages/components/LandingPage/LandingPageDefault';
import {
  appendUpdatedAtString,
  generateInternalMenus,
  getStructuredLocalBusinessWithAggregateReviewData,
  getLandingPageTitleAndDescriptionWithPageInformation,
} from 'modules/marketing-pages/utils';
import { FAQPage, WithContext } from 'schema-dts';
import { ReduxProps } from './LandingPage.container';
import { VerticalPageJobType } from 'modules/job-types/types';
import { RootState } from 'store/types';
import type { WordPressBlogPost } from 'modules/marketing-pages/marketing-page.types';
import { NUM_PROS_PER_PAGE } from '../../ListPros';
import {
  caseStudiesPageSizeSelector,
  caseStudiesTotalCountSelector,
} from 'modules/marketing-pages/duck/selectors';
import { useRouter } from 'next/router';

const MINIMUM_NUMBER_OF_PROS_FOR_SEO_INDEXING = 6;

const getContentData = memoize(
  ({
    cities = [],
    city = { _id: '', id: '', key: '', name: '' },
    cityGroup = '',
    cityGroupName = '',
    cityGroups = [],
    jobType,
    language = 'ja',
    pageType = 'jobType',
    popularJobTypes = [],
    prefecture = { _id: '', id: '', key: '', name: '' },
    prefectures = [],
    numberOfPros,
    relatedJobTypes = [],
    t,
    translation,
  }: Pick<
    CombinedProps,
    | 'cities'
    | 'city'
    | 'cityGroup'
    | 'cityGroupName'
    | 'cityGroups'
    | 'jobType'
    | 'pageType'
    | 'popularJobTypes'
    | 'prefecture'
    | 'prefectures'
    | 'relatedJobTypes'
    | 'translation'
  > & { language: string; numberOfPros: number; t: TFunction }) => {
    const prefix = language === 'ja' ? '' : `/${language}`;
    const articleEnabled = Boolean(jobType.seo?.article?.enabled && jobType.seo.article.content);

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const jobTypeName = jobType.translations.seoName || jobType.translations.requestType;

    const crumbs = [];
    let currentPath = prefix;
    for (const jobCategory of jobType.jobCategories ?? []) {
      currentPath = `${currentPath}/${jobCategory.id}`;
      crumbs.push({
        link: currentPath,
        text: jobCategory.title,
      });
    }

    if (jobTypeName) {
      crumbs.push({
        link: `${prefix}/${jobType.categories.join('/')}/${jobType.id}`,
        text: jobTypeName,
      });
    }
    if (prefecture.name)
      crumbs.push({
        link: `${prefix}/${jobType.categories.join('/')}/${jobType.id}/${prefecture.key}`,
        text: prefecture.name,
      });
    if (cityGroupName)
      crumbs.push({
        link: `${prefix}/${jobType.categories.join('/')}/${jobType.id}/${
          prefecture.key
        }/${cityGroup}`,
        text: cityGroupName,
      });
    if (city.name)
      crumbs.push({
        link: `${prefix}/${jobType.categories.join('/')}/${jobType.id}/${prefecture.key}/${
          city.key
        }`,
        text: city.name,
      });

    const landingPageTitle = jobType.seo?.landingPage?.title;
    const landingPageDescription = jobType.seo?.landingPage?.description;

    let title = landingPageTitle;
    const seo = {
      canonicalUrl: '',
      description: landingPageDescription,
      noFollow: jobType.seo?.landingPage?.noFollow,
      noIndex: jobType.seo?.landingPage?.noIndex,
      ogDescription: landingPageDescription,
      ogTitle: landingPageTitle,
    };

    if (articleEnabled) {
      title = jobType.seo?.article?.title;
      seo.description = jobType.seo?.article?.description;
    }

    if (!title) {
      let pref = prefecture.name;
      if (pageType === 'group') {
        pref += cityGroupName;
      }

      title = sprintf(translation.bannerTitle, {
        city: city.name,
        jobType: jobTypeName,
        prefecture: pref,
      });
    }

    if (!seo.description) {
      seo.description = t(`marketingPages:seo.description.${pageType}`, {
        city: city.name,
        jobType: jobTypeName,
        prefecture: prefecture.name,
        topProsNumber: 10,
      });
    }

    if (numberOfPros && numberOfPros < MINIMUM_NUMBER_OF_PROS_FOR_SEO_INDEXING) {
      seo.noIndex = true;
    }

    return {
      articleEnabled,
      crumbs,
      faqs: jobType.faqs ?? [],
      groups: generateInternalMenus({
        cities,
        city,
        cityGroups,
        jobType,
        language,
        pageType,
        popularJobTypes,
        prefecture,
        prefectures,
        relatedJobTypes,
        t,
      }),
      seo,
      title: appendUpdatedAtString({ language, title }),
    };
  }
);

export const getFAQStructuredData = memoize(
  (faqs: Array<{ answer: string; question: string }>): string => {
    const structuredData: WithContext<FAQPage> = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        name: faq.question,
      })),
    };

    return JSON.stringify(structuredData);
  }
);

interface Props {
  cityGroup?: string;
  cityGroupName?: string;
  featureFlags: { isReviewsQAEnabled: boolean };
  groupLink?: string;
  initialPageNumber: number;
  initialPageNumberForCaseStudy: number;
  jobType: VerticalPageJobType;
  numOfCityPros: number;
  numOfGroupPros: number;
  online?: boolean;
  pageTranslationId: 'areaPrefectureCityJobTypePage' | 'areaPrefectureVertical' | 'verticalLP';
  pageType: 'jobType' | 'prefecture' | 'city' | 'category' | 'group' | 'groupCity';
  posts?: Array<WordPressBlogPost>;
  shouldOpenRequestModal?: boolean;
  totalPros: number;
}

type CombinedProps = Props & ReduxProps;

const LandingPage: FC<CombinedProps> = ({
  cities,
  city,
  cityGroup,
  cityGroupName,
  cityGroups,
  numOfCityPros,
  numOfGroupPros,
  totalPros,
  featureFlags,
  groupLink,
  dispatch,
  initialPageNumber,
  initialPageNumberForCaseStudy,
  jobType,
  online,
  pageType,
  pageTranslationId,
  popularJobTypes,
  posts,
  prefecture,
  prefectures,
  proBlogPosts,
  proReviewStatistics,
  relatedJobTypes,
  shouldOpenRequestModal,
  translation,
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const router = useRouter();

  // Needed since query params can change during pagination
  const [currentPage, setCurrentPage] = useState(initialPageNumber);

  let isFirstPage = currentPage === 1;

  const [currentCaseStudiesPage, setCurrentCaseStudiesPage] = useState(
    initialPageNumberForCaseStudy
  );

  useEffect(() => {
    if (shouldOpenRequestModal) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        await openNewRequestModal({
          dispatch,
          properties: { jobTypeId: jobType.id },
        });
      })();
    }
  }, [dispatch, jobType.id, shouldOpenRequestModal]);

  useEffect(() => {
    isFirstPage = currentPage === 1;
  }, [currentPage]);

  const newestReviews = useSelector((state: RootState) => state.marketingPages.newestReviews);

  const { articleEnabled, crumbs, faqs, groups, seo, title } = getContentData({
    cities,
    city,
    cityGroup,
    cityGroupName,
    cityGroups,
    jobType,
    language,
    numberOfPros: totalPros,
    pageType,
    popularJobTypes,
    prefecture,
    prefectures,
    relatedJobTypes,
    t,
    translation,
  });

  const handlePageChange = useCallback<LandingPageDefaultProps['updateProPageCount']>((page) => {
    setCurrentPage(page);
    isFirstPage = page === 1;
  }, []);

  const handleCaseStudiesPageChange = useCallback<
    LandingPageDefaultProps['onCaseStudiesPageChange']
  >((currentCaseStudiesPage) => {
    setCurrentCaseStudiesPage(currentCaseStudiesPage);
  }, []);

  const titleAndDescription = getLandingPageTitleAndDescriptionWithPageInformation(
    title,
    seo.description ?? '',
    language,
    currentPage,
    currentCaseStudiesPage
  );

  // If the specified pro page number is fatigued with Max value, add noIndex.
  // If the specified caseStudy page number is fatigued with Max value, add noIndex.
  const totalCaseStudies = useSelector(caseStudiesTotalCountSelector);
  const caseStudyListSize = useSelector(caseStudiesPageSizeSelector);
  if (totalPros !== 0 && initialPageNumber > Math.ceil(totalPros / NUM_PROS_PER_PAGE)) {
    seo.noIndex = true;
  }
  if (
    totalCaseStudies !== 0 &&
    caseStudyListSize !== 0 &&
    initialPageNumberForCaseStudy > Math.ceil(totalCaseStudies / caseStudyListSize)
  ) {
    seo.noIndex = true;
  }

  /** If the content displayed on the page is equal to the initial display,
   * direct the canonical to the path of the initial display. */

  const { page, caseStudyPage } = router.query;
  const canonicalUrl = router.asPath;

  if (page === '1' && caseStudyPage === undefined) {
    seo.canonicalUrl = canonicalUrl;
  }
  if (page === undefined && caseStudyPage === '1') {
    seo.canonicalUrl = canonicalUrl;
  }
  if (page === '1' && caseStudyPage === '1') {
    seo.canonicalUrl = canonicalUrl;
  }
  if (router.asPath.endsWith('?')) {
    seo.canonicalUrl = canonicalUrl;
  }

  return (
    <Layout
      hasHrefLang={pageType !== 'city'}
      language={language}
      seo={{
        ...seo,
        description: titleAndDescription.description,
      }}
      shouldIncludeUrlParams={currentPage > 1}
      title={titleAndDescription.title}
    >
      <Head>
        <script
          type="application/ld+json"
          key="structured-review-data"
          dangerouslySetInnerHTML={{
            __html: getStructuredLocalBusinessWithAggregateReviewData(
              jobType.translations.requestType,
              proReviewStatistics
            ),
          }}
        />
        {faqs.length && (
          <script
            type="application/ld+json"
            key="faq-structured-data"
            dangerouslySetInnerHTML={{ __html: getFAQStructuredData(faqs) }}
          />
        )}
      </Head>
      <LandingPageDefault
        articleEnabled={articleEnabled}
        cities={cities}
        city={city}
        featureFlags={featureFlags}
        cityGroup={cityGroup}
        cityGroupName={cityGroupName}
        groupLink={groupLink}
        crumbs={crumbs}
        groups={groups}
        jobType={jobType}
        online={online}
        newestReviews={newestReviews}
        pageTranslationId={pageTranslationId}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        posts={posts}
        prefecture={prefecture}
        prefectures={prefectures}
        proBlogPosts={proBlogPosts}
        proPageCount={currentPage}
        currentCaseStudiesPage={currentCaseStudiesPage}
        popularJobTypes={popularJobTypes}
        title={title}
        updateProPageCount={handlePageChange}
        onCaseStudiesPageChange={handleCaseStudiesPageChange}
        isFirstPage={isFirstPage}
        numOfCityPros={numOfCityPros}
        numOfGroupPros={numOfGroupPros}
      />
    </Layout>
  );
};

export type { Props as LandingPageProps };
export default LandingPage;
